import { imageUrl } from '@helpers/image';
import { sleep } from '@helpers/utils';
import { withMotion } from '@hoc/withMotion';
import { useInView } from '@hooks/useInView';
import Breadcrumb from '@stories/Components/Breadcrumb/Breadcrumb';
import CarouselControls from '@stories/Components/CarouselControls/CarouselControls';
import CarouselProvider from '@stories/Components/CarouselControls/CarouselProvider';
import SiteWide from '@stories/Components/Global/SiteWide/SiteWide';
import Swipeable from '@stories/Components/Swipeable/Swipeable';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player/lazy';
import S from './HeroInnerBanner.styles';
export var MediaType;
(function (MediaType) {
    MediaType[MediaType["Image"] = 0] = "Image";
    MediaType[MediaType["Video"] = 1] = "Video";
})(MediaType || (MediaType = {}));
const HeroInnerBanner = ({ breadcrumb, items, title, transitionDelay = 1000, transitionDuration = 1000, autoplay = 0, loop = false, }) => {
    const [refContainer, isInView] = useInView({});
    const [activeIndex, setActiveIndex] = useState(0);
    const [ready, setReady] = useState(false);
    const [animateSlide, setAnimateSlide] = useState(false);
    const activeItem = items[activeIndex];
    const isExternal = !!activeItem.video &&
        (activeItem.video.indexOf('youtube') > -1 || activeItem.video.indexOf('vimeo') > -1);
    const slideTransitionDelay = 500;
    useEffect(() => {
        if (transitionDelay) {
            sleep(transitionDelay).then(() => {
                return () => setReady(true);
            });
        }
        return () => setReady(true);
    }, []);
    useEffect(() => {
        sleep(slideTransitionDelay).then(() => {
            setAnimateSlide(false);
        });
    }, [animateSlide === true]);
    function prev() {
        if (loop && activeIndex === 0) {
            return setActiveIndex(items.length - 1);
        }
        setActiveIndex(Math.max(activeIndex - 1, 0));
    }
    function next() {
        if (loop && activeIndex === items.length - 1) {
            return setActiveIndex(0);
        }
        setActiveIndex(Math.min(activeIndex + 1, items.length - 1));
    }
    const renderFallbackImage = () => {
        if (activeItem.fallbackImage === undefined) {
            return undefined;
        }
        return (React.createElement(S.Image, { key: activeItem.id, initial: "hidden", animate: ready ? 'fadeIn' : 'visible', exit: "fadeOut", variants: {
                hidden: {
                    opacity: 0,
                    scale: 1,
                },
                visible: {
                    opacity: 1,
                    scale: 1.1,
                    transition: {
                        type: 'tween',
                        duration: (transitionDuration + transitionDelay) / 1000,
                        ease: 'easeOut',
                    },
                },
                fadeIn: {
                    opacity: 1,
                    scale: 1.1,
                },
                fadeOut: {
                    opacity: 0,
                    scale: 1.1,
                },
            }, alt: "", loading: "eager", role: "presentation", src: imageUrl(activeItem.fallbackImage, { width: 480, heightratio: 1.5 }), srcSet: getImageSrcSet(activeItem.fallbackImage), sizes: "100vw" }));
    };
    const renderMedia = () => {
        return (React.createElement(CarouselProvider, { autoplay: activeItem.duration ?? autoplay, loop: loop, max: items.length, value: activeIndex, onChange: (index) => {
                setAnimateSlide(true);
                sleep(slideTransitionDelay).then(() => {
                    setActiveIndex(index);
                });
            } }, ({ actions: { playPause } }) => (React.createElement(Swipeable, { preventDefaultTouchmoveEvent: true, onSwipedLeft: () => {
                setAnimateSlide(true);
                sleep(slideTransitionDelay).then(() => {
                    playPause(true);
                    next();
                });
            }, onSwipedRight: () => {
                setAnimateSlide(true);
                sleep(slideTransitionDelay).then(() => {
                    playPause(true);
                    prev();
                });
            } }, (handlers) => (React.createElement(React.Fragment, null,
            React.createElement(S.MediaWrapper, { ...handlers },
                React.createElement(AnimatePresence, null,
                    activeItem.id && activeItem.mediaType === MediaType.Image && activeItem.image && (React.createElement(S.Image, { key: activeItem.id, initial: "hidden", animate: ready ? 'fadeIn' : 'visible', exit: "fadeOut", variants: {
                            hidden: {
                                opacity: 0,
                                scale: 1,
                            },
                            visible: {
                                opacity: 1,
                                scale: 1.1,
                                transition: {
                                    type: 'tween',
                                    duration: (transitionDuration + transitionDelay) / 1000,
                                    ease: 'easeOut',
                                },
                            },
                            fadeIn: {
                                opacity: 1,
                                scale: 1.1,
                            },
                            fadeOut: {
                                opacity: 0,
                                scale: 1.1,
                            },
                        }, alt: "", loading: "eager", role: "presentation", src: imageUrl(activeItem.image, { width: 480, heightratio: 1.5 }), srcSet: getImageSrcSet(activeItem.image), sizes: "100vw" })),
                    activeItem.id && activeItem.mediaType === MediaType.Video && activeItem.video && (React.createElement(S.Player, { key: activeItem.id, initial: "hidden", animate: ready ? 'fadeIn' : 'visible', exit: "fadeOut", variants: {
                            hidden: {
                                opacity: 0,
                                scale: 1,
                            },
                            visible: {
                                opacity: 1,
                                scale: 1.1,
                                transition: {
                                    type: 'tween',
                                    duration: (transitionDuration + transitionDelay) / 1000,
                                    ease: 'easeOut',
                                },
                            },
                            fadeIn: {
                                opacity: 1,
                                scale: 1.1,
                            },
                            fadeOut: {
                                opacity: 0,
                                scale: 1.1,
                            },
                        }, isExternal: isExternal },
                        React.createElement(ReactPlayer, { url: activeItem.video, controls: false, height: "100%", width: "100%", playing: true, playsinline: true, loop: true, muted: true, config: {
                                youtube: {
                                    playerVars: { rel: 0 },
                                },
                            }, fallback: renderFallbackImage() }))))),
            items.length > 1 && (React.createElement(S.Controls, { isActive: isInView, delay: transitionDelay },
                React.createElement(SiteWide, null,
                    React.createElement(CarouselControls, { controls: true, theme: 'dark' }))))))))));
    };
    return (React.createElement(S.Container, { ref: refContainer, flush: "both", fullWidth: true, themeOption: "dark" },
        React.createElement(S.Inner, { initial: "default", animate: "visible" },
            renderMedia(),
            React.createElement(S.Overlay, { variants: {
                    default: {
                        opacity: 0,
                    },
                    visible: {
                        opacity: 1,
                    },
                }, transition: {
                    type: 'tween',
                    duration: transitionDuration / 1000,
                    ease: 'easeOut',
                    delay: transitionDelay / 1000,
                } }),
            React.createElement(S.ContentWrapper, null,
                React.createElement(S.ContentWrapperInner, { variants: {
                        default: {
                            opacity: 0,
                        },
                        visible: {
                            opacity: 1,
                        },
                    }, transition: {
                        type: 'tween',
                        duration: 0.6,
                        ease: 'easeOut',
                        delay: transitionDelay / 1000 + 0.3,
                    } },
                    breadcrumb && (React.createElement(S.BreadcrumbWrapper, { variants: {
                            default: {
                                opacity: 0,
                                y: 20,
                            },
                            visible: {
                                opacity: 1,
                                y: 0,
                            },
                        }, transition: {
                            type: 'tween',
                            duration: 0.6,
                            ease: 'easeOut',
                            delay: transitionDelay / 1000 + 0.3,
                        } },
                        React.createElement(Breadcrumb, { items: breadcrumb }))),
                    title && (React.createElement(S.Title, { variants: {
                            default: {
                                opacity: 0,
                                y: 20,
                            },
                            visible: {
                                opacity: 1,
                                y: 0,
                            },
                        }, transition: {
                            type: 'tween',
                            duration: 0.6,
                            ease: 'easeOut',
                            delay: transitionDelay / 1000 + 0.6,
                        } }, title)),
                    React.createElement(S.Line, { style: {
                            x: '-50%',
                            y: 10,
                        }, variants: {
                            default: {
                                scaleX: 0,
                            },
                            visible: {
                                scaleX: 1,
                            },
                        }, transition: {
                            type: 'tween',
                            duration: 0.6,
                            ease: 'easeOut',
                            delay: transitionDelay / 1000 + 0.6,
                        } }))))));
};
function getImageSrcSet(image) {
    return [
        // Mobile/tablet need to be taller to avoid scaling
        `${imageUrl(image, { width: 480, heightratio: 1.5 })} 480w`,
        `${imageUrl(image, { width: 960, heightratio: 1.5 })} 960w`,
        // Desktop needs to be wider to avoid scaling
        `${imageUrl(image, { width: 1440, heightratio: 0.5625 })} 1440w`,
        `${imageUrl(image, { width: 1920, heightratio: 0.5625 })} 1920w`,
    ].join(',');
}
export default withMotion(HeroInnerBanner);
